import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { MainContainer, AuthBox, VerticalLayout, HorizontalLayout } from '../../common/common-layout'
import BTBox from '../../common/components/BTBox'
import { TitleNormal } from '../../common/common-text'
import { loginWithUsername } from '../../service/AuthService'
import { Button, Checkbox, FormControlLabel, TextField, CircularProgress } from "@mui/material";
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { LoginFormValues, loginSchema } from "../../schema/auth/LoginSchema"
import loginCi from "../../assets/icons/login_ci.svg"
import theme from '../../common/theme'
import BaseLayout from "../BaseLayout"
import { dialogStore } from "../../store/DialogStore"
import isFirebaseError from "../../common/firebase/FirebaseError"

const Login = () => {
    const navigate = useNavigate();
    const [error, setError] = useState("");
    const [rememberMe, setRememberMe] = useState(false);
    const [isLoading, setIsLoading] = useState(false); // ✅ 로딩 상태

    const handleLoginWithEmail = async (data: LoginFormValues) => {
        if (Object.keys(errors).length !== 0) {
            setError("입력Form 에러");
            return;
        }

        try {
            setIsLoading(true); // ✅ 시작
            await loginWithUsername(data.username, data.password);

            if (rememberMe) {
                localStorage.setItem("rememberedUsername", data.username);
            } else {
                localStorage.removeItem("rememberedUsername");
            }

            dialogStore.showDialog({
                title: '알림',
                message: '로그인 성공하였습니다.',
                onConfirm: () => navigate("/"),
                onClose: () => {},
            });
        } catch (error: any) {
            if (isFirebaseError(error)) {
                if (error.code === "auth/invalid-credential") {
                    dialogStore.showDialog({
                        title: '알림',
                        message: '아이디 또는 비밀번호가 잘못되었습니다.',
                        onConfirm: () => {
                            console.log('확인');
                        },
                        onClose: () => {
                            console.log('닫기');
                        },
                    });
                    return;
                } else {
                    dialogStore.showDialog({
                        title: '알림',
                        message: `로그인에 실패하였습니다. 에러: ${String(error)}`,
                        onConfirm: () => {},
                        onClose: () => {},
                    });
                }
            } else {
                dialogStore.showDialog({
                    title: '알림',
                    message: `로그인에 실패하였습니다. 에러: ${String(error)}`,
                    onConfirm: () => {},
                    onClose: () => {},
                });
            }
        } finally {
            setIsLoading(false); // ✅ 종료
        }
    };

    const handleFindUsername = () => {
        navigate("/find-username");
    };

    const handleChangePassword = () => {
        navigate("/change-password/step1");
    };

    const {
        register,
        handleSubmit,
        setValue,
        formState: { errors }
    } = useForm<LoginFormValues>({
        resolver: zodResolver(loginSchema)
    });

    useEffect(() => {
        const saved = localStorage.getItem("rememberMe");
        if (saved === "true") {
            setRememberMe(true);
        }

        const savedUsername = localStorage.getItem("rememberedUsername");
        if (savedUsername) {
            setValue("username", savedUsername);
        }
    }, [setValue]);

    return (
        <BaseLayout>
            <MainContainer backgroundColor='#14B446'>
                <AuthBox style={{ width: '516px' }}>
                    <VerticalLayout>
                        <form onSubmit={handleSubmit(handleLoginWithEmail)} style={{ width: '100%' }}>
                            <BTBox height="20px" />
                            <HorizontalLayout style={{ justifyContent: 'center', width: '100%' }}>
                                <img src={loginCi} />
                            </HorizontalLayout>
                            <BTBox height='50px' />
                            <TitleNormal color='#8a92a6'>아이디</TitleNormal>
                            <BTBox height='8px' />
                            <HorizontalLayout>
                                <TextField
                                    variant="outlined"
                                    fullWidth
                                    disabled={isLoading}                // ✅ 로딩 중 비활성화
                                    {...register("username")}
                                    error={!!errors.username}
                                    helperText={errors.username?.message}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                        '& fieldset': { borderColor: errors.username ? '#c03221' : theme.colors.bbTech },
                                        '&:hover fieldset': { borderColor: theme.colors.bbTech },
                                        '&.Mui-focused fieldset': { borderColor: theme.colors.bbTech },
                                        },
                                    }}
                                />
                            </HorizontalLayout>

                            <BTBox height='20px' />
                            <TitleNormal color='#8a92a6'>비밀번호</TitleNormal>
                            <BTBox height='8px' />
                            <HorizontalLayout>
                                <TextField
                                variant="outlined"
                                fullWidth
                                type="password"
                                disabled={isLoading}                // ✅ 로딩 중 비활성화
                                {...register("password")}
                                error={!!errors.password}
                                helperText={errors.password?.message}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                    '& fieldset': { borderColor: errors.username ? '#c03221' : theme.colors.bbTech },
                                    '&:hover fieldset': { borderColor: theme.colors.bbTech },
                                    '&.Mui-focused fieldset': { borderColor: theme.colors.bbTech },
                                    },
                                }}
                                />
                            </HorizontalLayout>

                            {error && <p style={{ color: "red" }}>{error}</p>}

                            <BTBox height='8px' />
                            <HorizontalLayout style={{ justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            defaultChecked={false}
                                            checked={rememberMe}
                                            disabled={isLoading}            // ✅ 로딩 중 비활성화
                                            onChange={(e) => {
                                                const checked = e.target.checked;
                                                setRememberMe(checked);
                                                localStorage.setItem("rememberMe", checked.toString());
                                            }}
                                            sx={{ color: '#8A92A6', '&.Mui-checked': { color: '#8A92A6' } }}
                                        />
                                    }
                                    label={<span style={{ fontSize: '16px', color: '#8A92A6' }}>아이디 저장</span>}
                                />
                                <HorizontalLayout wrapContents={true}>
                                    <Button
                                        type="button"
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            padding: 0,
                                            margin: 0,
                                            fontSize: '16px',
                                            color: '#3A57E8',
                                            cursor: 'pointer',
                                            textDecoration: 'none',
                                            outline: 'none',
                                            textTransform: 'none',
                                        }}
                                        onClick={() => {
                                            console.log("아이디 찾기 클릭됨");
                                            handleFindUsername();
                                        }}
                                    >
                                        아이디 찾기
                                    </Button>
                                    <BTBox width="16px" />
                                    <Button
                                        type="button"
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            padding: 0,
                                            margin: 0,
                                            fontSize: '16px',
                                            color: '#3A57E8',
                                            cursor: 'pointer',
                                            textDecoration: 'none',
                                            outline: 'none',
                                            textTransform: 'none',
                                        }}
                                        onClick={() => {
                                            console.log("비밀번호 찾기 클릭됨");
                                            handleChangePassword();
                                        }}
                                    >
                                        비밀번호 찾기
                                    </Button>
                                </HorizontalLayout>
                            </HorizontalLayout>
                            <BTBox height='24px' />

                            <Button
                                type="submit"
                                disabled={isLoading}                 // ✅ 중복 제출 방지
                                aria-busy={isLoading ? 'true' : 'false'}
                                style={{
                                    width: '100%',
                                    height: '59px',
                                    fontSize: '20px',
                                    fontWeight: 700,
                                    backgroundColor: theme.colors.bbTech,
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '10px',
                                    cursor: isLoading ? 'default' : 'pointer',
                                    outline: 'none',
                                }}
                            >
                                {isLoading ? (
                                <CircularProgress size={26} color="inherit" />
                                ) : (
                                '로그인'
                                )}
                            </Button>
                        </form>

                        <BTBox height='20px' />
                    </VerticalLayout>
                </AuthBox>
            </MainContainer>
        </BaseLayout>
    );
};

export default Login;